import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
    TextField, Button, Typography, Stack, Divider, Box, Tabs, Tab, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { API } from '../App';

export default function SelfSettingsModal({ modalRef, onSaved }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState('profile');
    const [profile, setProfile] = useState({ _id: null, name: '', description: '' });
    const [password, setPassword] = useState({ oldPassword: '', newPassword: '', confirm: '' });
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (modalRef) modalRef.current = { handleOpen, handleClose };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalRef]);

    const handleOpen = () => {
        const u = API.user || {};
        setProfile({ _id: u._id || null, name: u.name || '', description: u.description || '' });
        setPassword({ oldPassword: '', newPassword: '', confirm: '' });
        setError('');
        setInfo('');
        setTab('profile');
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setError('');
        setInfo('');
    };

    const submitProfile = async () => {
        setSaving(true); setError(''); setInfo('');
        try {
            const res = await API.updateUser({
                _id: profile._id,
                description: profile.description
            });
            if (!res?.success) { setError(res?.msg || 'Speichern fehlgeschlagen.'); return; }
            await API.getLoggedIn();
            setInfo('Profil aktualisiert.');
            if (onSaved) onSaved();
        } finally {
            setSaving(false);
        }
    };

    const submitPassword = async () => {
        if (!password.oldPassword) { setError('Altes Passwort ist erforderlich.'); return; }
        if (!password.newPassword) { setError('Neues Passwort ist erforderlich.'); return; }
        if (password.newPassword !== password.confirm) { setError('Die Passwörter stimmen nicht überein.'); return; }
        setSaving(true); setError(''); setInfo('');
        try {
            const res = await API.changePassword(profile._id, password.oldPassword, password.newPassword);
            if (!res?.success) { setError(res?.msg || 'Passwortänderung fehlgeschlagen.'); return; }
            setInfo('Passwort geändert.');
            setPassword({ oldPassword: '', newPassword: '', confirm: '' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
            <DialogTitle sx={{ pr: 6 }}>
                Mein Profil
                <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <Tabs
                value={tab}
                onChange={(_, v) => { setTab(v); setError(''); setInfo(''); }}
                textColor="primary"
                indicatorColor="primary"
                sx={{ px: 3 }}
            >
                <Tab value="profile" label="Profil" />
                <Tab value="password" label="Passwort" />
            </Tabs>
            <Divider />
            <DialogContent>
                {tab === 'profile' && (
                    <Stack spacing={2}>
                        <TextField
                            label="Benutzername"
                            value={profile.name}
                            disabled
                            fullWidth
                            size="medium"
                            helperText="Der Benutzername kann nur von einem Administrator geändert werden."
                        />
                        <TextField
                            label="Anzeigename"
                            value={profile.description}
                            onChange={(e) => setProfile({ ...profile, description: e.target.value })}
                            fullWidth
                            size="medium"
                            helperText="Vollständiger Name für Anzeige in Aufträgen (Ansprechpartner, Ausführender)."
                        />
                    </Stack>
                )}
                {tab === 'password' && (
                    <Stack spacing={2}>
                        <TextField
                            label="Aktuelles Passwort"
                            type="password"
                            value={password.oldPassword}
                            onChange={(e) => setPassword({ ...password, oldPassword: e.target.value })}
                            required
                            fullWidth
                            size="medium"
                            autoComplete="current-password"
                        />
                        <TextField
                            label="Neues Passwort"
                            type="password"
                            value={password.newPassword}
                            onChange={(e) => setPassword({ ...password, newPassword: e.target.value })}
                            required
                            fullWidth
                            size="medium"
                            autoComplete="new-password"
                        />
                        <TextField
                            label="Neues Passwort bestätigen"
                            type="password"
                            value={password.confirm}
                            onChange={(e) => setPassword({ ...password, confirm: e.target.value })}
                            required
                            fullWidth
                            size="medium"
                            autoComplete="new-password"
                        />
                    </Stack>
                )}

                {error && <Typography color="error" variant="body2" sx={{ mt: 2 }}>{error}</Typography>}
                {info && <Typography color="success.main" variant="body2" sx={{ mt: 2 }}>{info}</Typography>}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleClose} disabled={saving}>Schließen</Button>
                <Button
                    onClick={tab === 'profile' ? submitProfile : submitPassword}
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={saving}
                >
                    {saving ? 'Speichert…' : 'Speichern'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
